import {
  MEMBER_FIELD_KEY,
  buildWalletOptionsFields,
} from '@modules/consolidator/utils/build-wallet-options-fields/build-wallet-options-fields'

/** Chave que o builder por cliente usa para o campo de conta. */
const CLIENT_ACCOUNT_FIELD_KEY = 'idConta'

/**
 * No grupo, a chave de cada campo é o NOME DO PARÂMETRO DA API — o mesmo do
 * slot `familyGroupFilters` do store. Assim a barra de filtros do grupo não
 * precisa de camada de tradução entre nome de UI e nome de query param.
 */
export const GROUP_ACCOUNT_FIELD_KEY = 'id_conta'
export const GROUP_MEMBER_FIELD_KEY = MEMBER_FIELD_KEY
const GROUP_MEMBER_FIELD_LABEL = 'Membro'

/**
 * Monta os campos da barra de filtros a partir do `group_data` do OPTIONS da
 * rota de grupo familiar.
 *
 * Reaproveita `buildWalletOptionsFields` (caminho por cliente) para as
 * dimensões comuns e trata as duas diferenças do contrato de grupo:
 *
 * 1. `id_conta` chega como `{value, label}` (rótulo legível "Banco - número ·
 *    membro"), enquanto o builder por cliente espera `{id_conta, conta}`;
 * 2. existe a dimensão `id_cliente` (membro do grupo). Desde 07/09/2026 ela também é
 *    emitida por `buildWalletOptionsFields`, quando o OPTIONS traz a chave — este builder
 *    a monta com o rótulo próprio da tela antiga e por isso a exclui de `otherFields`,
 *    senão a dimensão sairia duplicada.
 *
 * Ordem: Conta primeiro (é o filtro de entrada da tela de grupo), Membro em
 * seguida, depois as demais dimensões na ordem já estabelecida no consolidador.
 *
 * @param {Object} groupData `group_data` da resposta do OPTIONS.
 * @returns {Array} Campos de filtro.
 */
export const buildGroupWalletOptionsFields = (groupData) => {
  const groups = resolveAvailableValues(groupData)
  const baseFields = buildWalletOptionsFields({
    group_by_available_values: {
      ...groups,
      id_conta: normalizeAccounts(groups.id_conta),
    },
  })

  const accountFields = baseFields
    .filter((field) => field.key === CLIENT_ACCOUNT_FIELD_KEY)
    .map((field) => ({ ...field, key: GROUP_ACCOUNT_FIELD_KEY }))
  const otherFields = baseFields.filter(
    (field) =>
      field.key !== CLIENT_ACCOUNT_FIELD_KEY &&
      field.key !== GROUP_MEMBER_FIELD_KEY
  )

  return [...accountFields, buildMemberField(groups.id_cliente), ...otherFields]
}

const resolveAvailableValues = (groupData) => {
  if (!groupData || typeof groupData !== 'object') return {}
  return groupData.group_by_available_values || {}
}

/**
 * Converte `{value, label}` do contrato de grupo para `{id_conta, conta}`, que
 * é o shape que `buildWalletOptionsFields` sabe ler. Aceita os dois formatos
 * para não quebrar caso o backend padronize um deles.
 */
const normalizeAccounts = (items) => {
  if (!Array.isArray(items)) return []
  return items
    .filter((item) => item && typeof item === 'object')
    .map((item) => ({
      id_conta: resolveOptionValue(item, 'id_conta'),
      conta: resolveOptionLabel(item, 'conta'),
    }))
}

const buildMemberField = (items) => ({
  key: GROUP_MEMBER_FIELD_KEY,
  label: GROUP_MEMBER_FIELD_LABEL,
  items: mapMembers(items),
  multiple: true,
  itemText: 'label',
  itemValue: 'id',
})

const mapMembers = (items) => {
  if (!Array.isArray(items)) return []
  return items
    .filter((item) => item && typeof item === 'object')
    .map((item) => ({
      id: resolveOptionValue(item, 'id_cliente'),
      label: resolveOptionLabel(item, 'nome'),
    }))
    .filter((member) => member.id !== null)
    .map((member) => ({
      id: member.id,
      label: member.label || String(member.id),
    }))
}

const resolveOptionValue = (item, legacyKey) => {
  const candidates = [item.value, item[legacyKey]]
  for (const candidate of candidates) {
    if (candidate !== undefined && candidate !== null) return candidate
  }
  return null
}

// Devolve `null` (e não string vazia) quando não há rótulo, para que quem
// consome caia no fallback do id em vez de exibir um item em branco.
const resolveOptionLabel = (item, legacyKey) => {
  const candidates = [item.label, item[legacyKey]]
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.length > 0) return candidate
  }
  return null
}
